import dayjs from 'dayjs';
import { FUEL_LEVEL_LITERS } from '../OnlinePanel/Widgets/FuelLevelLitersWidget';
import {
  URL_BATTERY_VOLTAGE,
  URL_FUEL_LEVEL_LITERS,
  URL_FUEL_LEVEL_PERCENTAGE,
  URL_RPM,
} from '../../router/constants';
import { FUEL_LEVEL_PERCENTAGE } from '../OnlinePanel/Widgets/FuelLevelPercentageWidget';
import { RPM } from '../OnlinePanel/Widgets/RpmWidget';
import { THROTTLE } from '../OnlinePanel/Widgets/ThrottleWidget';
import { VELOCITY } from '../OnlinePanel/Widgets/SpeedWidget';
import { BATTERY_VOLTAGE } from '../OnlinePanel/Widgets/BatteryWidget';
import { IVehicle } from '../Vehicles/types';
import showMessage from '../Toastr/ToastService';
import { getRequest, postRequest } from '../../Services/HttpService';
import { AxiosResponse } from 'axios';

export const GRAPH_LIMIT = 1024;
export const GRAPH_SELECTED_POINT_COLOR = '#389ded';
export const GRAPH_BORDER_COLOR = 'rgb(13, 51, 111)';
export const GRAPH_POINT_BORDER_COLOR = '#fff';
export const GRAPH_VIOLATION_POINT_COLOR = '#b50536';
export const GRAPH_COMPR_BACKGROUND_COLOR = 'rgba(61, 114, 200, 0.5)';
export const GRAPH_COMPR_BORDER_COLOR = 'rgb(61, 114, 200)';

export interface IGraphData {
  xLabels: string[];
  yLabels: (number | null)[];
  tooltips: Record<string, any>;
  minLabels: string[];
  maxLabels: string[];
}

export interface IGraphXYData {
  x: string;
  y: number | null;
}

export interface IGraphDataset {
  data: IGraphXYData[];
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  pointBackgroundColor: string | string[];
  pointBorderColor: string;
  pointRadius: number | number[];
  pointHoverRadius: number | number[];
  cubicInterpolationMode: string;
}

export interface IGraphCode {
  code: string;
  address: string;
  timestamp: string;
}

export interface IGraphResponseData {
  timestamp: string;
  value: number;
  violations: Record<string, any>[];
  min: string;
  max: string;
  address: string;
  latitude: string;
  longitude: string;
  distance?: string;
  code: string;
}

export const todayDateRange = [
  new Date(new Date().setHours(0, 0, 0, 0)),
  new Date(new Date().setHours(23, 59, 59, 999)),
];

export const getData = async (type: string, vehicleId: string) => {
  const url = `/v1/vehicle/${vehicleId}/graph/${type}`;
  const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
  if (response && response.data && response.data.length > 0 && response.status === 200) {
    return response.data;
  }
  return false;
};

export const getPointsData = (graphData: IGraphData) => {
  const pointsData: { timestamp: string; address: string; code: string }[] = [];
  graphData.xLabels.forEach((timestamp) => {
    pointsData.push({
      timestamp,
      address: graphData.tooltips[timestamp]?.address,
      code: graphData.tooltips[timestamp]?.code,
    });
  });
  return pointsData;
};

export const getPositionDetail = async (vehicleId: number, code: string) => {
  const url = `/v1/vehicle/${vehicleId}/position/${code}`;
  const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
  if (response && response.data && response.status === 200) {
    return response.data;
  }
  return false;
};

const getIsMinDiff = (dateFrom: Date, dateTo: Date) => {
  dateFrom.setSeconds(0);
  dateFrom.setMilliseconds(0);
  dateTo.setSeconds(0);
  dateTo.setMilliseconds(0);
  if (new Date(dateFrom).getTime() + 60000 <= new Date(dateTo).getTime()) {
    return true;
  }
  return false;
};

export const getVehicleData = async (vehicleId: number) => {
  const url = `/v1/vehicle/${vehicleId}`;
  const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
  if (
    response &&
    response.data &&
    response.data.vehicle &&
    response.data.vehicle.lastData &&
    response.status === 200
  ) {
    return response.data.vehicle.lastData.ignitionOnFirstTs;
  }
  return false;
};

export const getMinutesGraphHeight = (graphData: IGraphData, isDetail: boolean) => {
  if (
    graphData &&
    graphData.xLabels.length > 1 &&
    getIsMinDiff(
      new Date(graphData.xLabels[0]),
      new Date(graphData.xLabels[graphData.xLabels.length - 1])
    )
  ) {
    return isDetail ? '408px' : '200px';
  }
  return isDetail ? '393px' : '185px';
};

export const getVehicleSpeedLimits = async () => {
  const url = `/v1/user-settings/ride_player`;
  const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
  if (
    response &&
    response.data &&
    response.data.settings &&
    response.data.settings.value &&
    response.data.settings.value.speedLimits &&
    response.data.settings.value.speedLimits.limits &&
    response.status === 200
  ) {
    return response.data.settings.value.speedLimits.limits;
  }
  return false;
};

export const getTanksCapacity = async (id: number) => {
  if (id) {
    const url = `/v1/vehicle/${id}`;
    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (
      response &&
      response.data &&
      response.data.vehicle &&
      response.data.vehicle.tanks &&
      response.status === 200
    ) {
      const tank = response.data.vehicle.tanks.find((i) => i.primary === true);
      if (tank) {
        return tank.totalCapacity;
      }
      return false;
    }
    return false;
  }
  return false;
};

export const getPartData = async (type: string, vehicleId: string, dates?: Date[]) => {
  if (vehicleId && vehicleId !== '0') {
    const dateFrom =
      dates && dates.length === 2
        ? new Date(dates[0]).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    const dateTo =
      dates && dates.length === 2
        ? new Date(dates[1]).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
    const url = `/v1/vehicle/${vehicleId}/graph/${type}?from=${dateFrom}&to=${dateTo}`;

    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getDataByRideId = async (type: string, vehicleId: string, rideId: string) => {
  if (vehicleId && vehicleId !== '0') {
    const url = `/v1/vehicle/${vehicleId}/graph/${type}?rideId=${rideId}`;
    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (response && response.data && response.data.length > 0 && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getPartDataByRideId = async (
  type: string,
  vehicleId: string,
  rideId: string,
  dates?: Date[]
) => {
  if (vehicleId && vehicleId !== '0') {
    const dateFrom =
      dates && dates.length === 2
        ? new Date(dates[0]).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    const dateTo =
      dates && dates.length === 2
        ? new Date(dates[1]).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
    const url = `/v1/vehicle/${vehicleId}/graph/${type}?rideId=${rideId}&from=${dateFrom}&to=${dateTo}`;

    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getDataByDates = async (type: string, vehicleId: string, dates?: Date[]) => {
  if (vehicleId && vehicleId !== '0') {
    const dateFrom =
      dates && dates.length === 2
        ? new Date(dates[0]).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    const dateTo =
      dates && dates.length === 2
        ? new Date(dates[1]).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
    const url = `/v1/vehicle/${vehicleId}/graph/${type}?dateFrom=${dateFrom}&dateTo=${dateTo}`;

    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getPartDataByDates = async (
  type: string,
  vehicleId: string,
  dates?: Date[],
  partDates?: Date[]
) => {
  if (vehicleId && vehicleId !== '0') {
    const dateFrom =
      dates && dates.length === 2
        ? new Date(dates[0]).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    const dateTo =
      dates && dates.length === 2
        ? new Date(dates[1]).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
    const partDateFrom =
      partDates && partDates.length === 2
        ? new Date(partDates[0]).toISOString()
        : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
    const partDateTo =
      partDates && partDates.length === 2
        ? new Date(partDates[1]).toISOString()
        : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();
    const url = `/v1/vehicle/${vehicleId}/graph/${type}?dateFrom=${dateFrom}&dateTo=${dateTo}&from=${partDateFrom}&to=${partDateTo}`;

    const response: AxiosResponse | false = await getRequest(`${url}`, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getIsCompressed = (responseData: IGraphResponseData[]) => {
  let isCompressed = false;
  if (responseData) {
    responseData.forEach((item) => {
      if (item.min || item.max) {
        isCompressed = true;
      }
    });
    return isCompressed;
  }
  return isCompressed;
};

export const getVehicleName = (vehicle: IVehicle) => {
  return `(${vehicle.licenceNumber || ''} - ${vehicle.name || ''})`;
};

export const getSubpageVehicleName = (vehicle: IVehicle) => {
  return `${vehicle.licenceNumber || ''} - ${vehicle.name || ''}`;
};

export const getRideStartDateAndAddress = (responseData: IGraphResponseData[]) => {
  if (responseData && responseData[0]) {
    const position = `${responseData[0].latitude}, ${responseData[0].longitude}`;
    return `${dayjs(responseData[0].timestamp).format('DD.MM.YYYY HH:mm')} - ${
      responseData[0].address || position
    }`;
  }
  return '';
};

export const getRideEndDateAndAddress = (responseData: IGraphResponseData[]) => {
  let endAddress = '';
  const count = Object.keys(responseData).length - 1;
  if (responseData && responseData[count]) {
    const position = `${responseData[count].latitude}, ${responseData[count].longitude}`;
    endAddress = `${dayjs(responseData[count].timestamp).format('DD.MM.YYYY HH:mm')} - ${
      responseData[count].address || position
    }`;
  }
  return endAddress;
};

export const getRideStartDate = (responseData: IGraphResponseData[]) => {
  if (responseData && responseData[0]) {
    return `${dayjs(responseData[0].timestamp).format('DD.MM.YYYY HH:mm')}`;
  }
  return '';
};

export const getRideEndDate = (responseData: IGraphResponseData[]) => {
  let endAddress = '';
  const count = Object.keys(responseData).length - 1;
  if (responseData && responseData[count]) {
    endAddress = `${dayjs(responseData[count].timestamp).format('DD.MM.YYYY HH:mm')}`;
  }
  return endAddress;
};

const getLimitedValues = (values: string[]) => {
  const newData: string[] = [];
  Object.keys(values).forEach((key) => {
    if (String(key) !== '0') {
      newData.push(values[key]);
    }
  });
  return newData;
};

const getLimitedNumbers = (values: (number | null)[]) => {
  const newData: number[] = [];
  Object.keys(values).forEach((key) => {
    if (String(key) !== '0') {
      newData.push(values[key]);
    }
  });
  return newData;
};

const getLimitedObjectValues = (values) => {
  const newData = { ...values };
  const firstTimestamp = Object.keys(values)[0];
  if (!newData[firstTimestamp].violations) {
    delete newData[firstTimestamp];
  }
  return newData;
};

export const getPointsCount = (
  responseData: IGraphResponseData[],
  ignitionOnFirstTs: string | null
) => {
  if (
    responseData.length === GRAPH_LIMIT &&
    ignitionOnFirstTs !== null &&
    String(ignitionOnFirstTs) !== String(responseData[0]?.timestamp)
  ) {
    return GRAPH_LIMIT + 1;
  }
  return responseData.length;
};

export const hadleGraphDataAccordingToLimit = (tempGraphData: IGraphData, count: number) => {
  if (tempGraphData && count > GRAPH_LIMIT) {
    tempGraphData.xLabels = getLimitedValues(tempGraphData.xLabels);
    tempGraphData.yLabels = getLimitedNumbers(tempGraphData.yLabels);
    tempGraphData.minLabels = getLimitedValues(tempGraphData.minLabels);
    tempGraphData.maxLabels = getLimitedValues(tempGraphData.maxLabels);
    tempGraphData.tooltips = getLimitedObjectValues(tempGraphData.tooltips);
    return tempGraphData;
  }
  return tempGraphData;
};

export const getViolationsData = async (vehicleId: number) => {
  if (vehicleId) {
    const url = `/v1/ride-eco-drive-violation/list-data`;
    const input = {
      filter: { rideId: null, vehicleId },
      sort: { timestamp: 'desc' },
      page: 1,
      paging: 10,
    };
    const response: AxiosResponse | false = await postRequest(url, input, { returnAxiosResponse: true });
    if (response && response.data && response.status === 200) {
      return response.data;
    }
    return false;
  }
  return false;
};

export const getUrl = (name: string) => {
  if (name === FUEL_LEVEL_LITERS) {
    return URL_FUEL_LEVEL_LITERS;
  }
  if (name === RPM) {
    return URL_RPM;
  }
  if (name === BATTERY_VOLTAGE) {
    return URL_BATTERY_VOLTAGE;
  }
  if (name === FUEL_LEVEL_PERCENTAGE) {
    return URL_FUEL_LEVEL_PERCENTAGE;
  }
  return '';
};

export const getUnit = (name: string) => {
  if (name === FUEL_LEVEL_LITERS) {
    return 'L';
  }
  if (name === BATTERY_VOLTAGE) {
    return 'V';
  }
  if (name === RPM) {
    return 'ot/min';
  }
  return '';
};

export const getOnlinePanelParam = (name: string) => {
  if (name === FUEL_LEVEL_LITERS) {
    return 'fuelLevelLiters';
  }
  if (name === RPM) {
    return 'rpm';
  }
  if (name === BATTERY_VOLTAGE) {
    return 'battery';
  }
  return '';
};

export const getNameFromUrl = (name: string) => {
  if (name === URL_FUEL_LEVEL_LITERS) {
    return FUEL_LEVEL_LITERS;
  }
  if (name === URL_FUEL_LEVEL_PERCENTAGE) {
    return FUEL_LEVEL_PERCENTAGE;
  }
  if (name === URL_BATTERY_VOLTAGE) {
    return BATTERY_VOLTAGE;
  }
  if (name === URL_RPM) {
    return RPM;
  }
  return '';
};

export const getIsMoreThanOneDay = (dates: Date[]) => {
  if (dates && dates.length === 2) {
    if (new Date(dates[1]).getTime() - new Date(dates[0]).getTime() >= 86400000) {
      return true;
    }
    return false;
  }
  return false;
};

export const getGraphGdpr = (isVehicleGdprDataAllowed, vehicleId, f) => {
  if (vehicleId && Number.isInteger(vehicleId) && isVehicleGdprDataAllowed) {
    const allowed = isVehicleGdprDataAllowed[vehicleId];
    if (!allowed) {
      showMessage(f({ id: 'toastr.info' }), f({ id: 'graph.toastr.gdpr.message' }), 'info');
    }
    return allowed;
  }
  return true;
};

export const getIsEndDateToday = (dateTo: Date) => {
  const now = new Date();
  if (
    // today.getDate() === date.getDate() &&
    // today.getMonth() === date.getMonth() &&
    // today.getFullYear() === date.getFullYear() &&
    // date.getHours() === 23 &&
    // date.getMinutes() === 59
    dateTo > now
  ) {
    return true;
  }
  return false;
};

export const getIsFilterableByRideId = (name: string) => {
  if (name === RPM) {
    return true;
  }
  return false;
};

const getTooltipTitle = (name, f) => {
  if (name === FUEL_LEVEL_LITERS) {
    return f({ id: 'widget.fuelLevel.tooltip' }, { valueType: 'L' });
  }
  if (name === RPM) {
    return f({ id: 'infoWindow.data.engineRpm' });
  }
  if (name === THROTTLE) {
    return f({
      id: `enum.onlineMap.widget.types.throttle`,
    });
  }
  if (name === FUEL_LEVEL_PERCENTAGE) {
    return f(
      {
        id: `widget.fuelLevel.tooltip`,
      },
      { valueType: '%' }
    );
  }
  if (name === VELOCITY) {
    return f({
      id: `widget.speed.valueName.short`,
    });
  }
  if (name === BATTERY_VOLTAGE) {
    return f({ id: 'infoWindow.data.voltage' });
  }
  return f({
    id: `graph.${name}.page.title`,
  });
};

export const getTooltipSubtitle = (dataPoints, f) => {
  if (dataPoints && dataPoints[0]) {
    const datasetIndex = dataPoints[0].datasetIndex;
    if (datasetIndex === 0) {
      return ` (${f({ id: 'graph.tooltip.median' })})`;
    }
    if (datasetIndex === 1) {
      return ` (min)`;
    }
    if (datasetIndex === 2) {
      return ` (max)`;
    }
    return '';
  }
  return '';
};

export const renderGraphTooltip = (tooltipModel, ref, tooltips, name, unit, f, isCompressed) => {
  let graphCode: IGraphCode | null = null;

  // Tooltip Element
  let tooltipEl = document.getElementById('chartjs-tooltip');

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-tooltip';
    tooltipEl.innerHTML = `<div><div id="point"></div><table></table></div>`;
    document.body.appendChild(tooltipEl);
    tooltipEl.style.zIndex = '999';
  }

  // Hide if no tooltip
  if (String(tooltipModel.opacity) === '0') {
    tooltipEl.style.opacity = '0';
    return null;
  }

  // Set caret Position
  tooltipEl.classList.remove('above', 'below', 'no-transform');
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add('no-transform');
  }

  const getBody = (bodyItem) => {
    return bodyItem.lines;
  };

  // Set Text
  if (tooltipModel.body) {
    const titleLines = tooltipModel.title || [];
    const bodyLines = tooltipModel.body.map(getBody);

    let innerHtml = '<thead>';
    let xLabel = '';
    let yLabel = '';
    let address = '';
    let lineIndex = '';

    titleLines.forEach((title: string) => {
      innerHtml += `<tr><th> </th><th> </th></tr>`;
      xLabel = dayjs(title).format('DD.MM.YYYY HH:mm:ss');
      lineIndex = title;
    });
    innerHtml += '</thead><tbody>';

    bodyLines.forEach((body: string, i: number) => {
      yLabel = Number(body).toFixed(1);
    });

    innerHtml += `<tr><td>${getTooltipTitle(name, f)}${
      isCompressed ? getTooltipSubtitle(tooltipModel?.dataPoints, f) : ''
    }:&nbsp;</td><td><strong>${yLabel} ${unit}</strong></td></tr>`;
    innerHtml += `<tr><td>${f({
      id: `onlinepanel.date`,
    })}:&nbsp;</td><td><strong>${xLabel}</strong></td></tr>`;

    if (tooltips && tooltips[lineIndex]) {
      address = tooltips[lineIndex].address
        ? `<tr><td>${f({
            id: `infoWindow.adress`,
          })}&nbsp;</td><td><strong>${tooltips[lineIndex].address}</strong></td></tr>`
        : '';
      graphCode = {
        code: tooltips[lineIndex].code,
        address: tooltips[lineIndex].address,
        timestamp: lineIndex,
      };
    }

    innerHtml += address;

    innerHtml += '</tbody>';

    const tableRoot = tooltipEl.querySelector('table');
    if (tableRoot) {
      tableRoot.innerHTML = innerHtml;
    }
  }

  // `this` will be the overall tooltip
  const position = ref._chart.canvas.getBoundingClientRect();
  const point: any = document.getElementById('point');

  // Display, position, and set styles for font
  const shift = window.innerWidth - tooltipModel.caretX < 500 ? 214 : 0;
  if (point) {
    point.style =
      window.innerWidth - tooltipModel.caretX < 500
        ? 'width: 13px; height: 13px; position: absolute; background: #389ded; top: -8px; right: -15px; border: 4px solid #fff; border-radius: 50%;'
        : 'width: 13px; height: 13px; position: absolute; background: #389ded; top: -8px; left: -15px; border: 4px solid #fff; border-radius: 50%;';
  }
  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.left = `${
    7 + position.left + window.pageXOffset + tooltipModel.caretX - shift
  }px`;
  tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
  tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
  tooltipEl.style.fontSize = `${tooltipModel.bodyFontSize}px`;
  tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
  tooltipEl.style.padding = `${tooltipModel.yPadding}px ${tooltipModel.xPadding}px`;
  tooltipEl.style.pointerEvents = 'none';
  tooltipEl.style.background = '#494949';
  tooltipEl.style.color = '#fff';
  tooltipEl.style.border = '1px solid #ccc';
  tooltipEl.style.borderRadius = '3px';
  tooltipEl.style.width = '200px';
  tooltipEl.style.cursor = 'pointer';
  return graphCode;
};

const getLineValue = (scale, index, offsetGridLines) => {
  let lineValue = scale.getPixelForTick(index);
  if (offsetGridLines) {
    if (index === 0) {
      lineValue -= (scale.getPixelForTick(1) - lineValue) / 2;
    } else {
      lineValue -= (lineValue - scale.getPixelForTick(index - 1)) / 2;
    }
  }
  return lineValue;
};

export const plugin = {
  id: 'gridBgColor',
  afterDraw(chartInstance) {
    const yScale = chartInstance.scales['y-axis-0'];
    const canvas = chartInstance.chart;
    const ctx = canvas.ctx;
    let index = 0;
    let line: { style: string; text: string; y: string } = { style: '', text: '', y: '' };
    let style = '';
    if (chartInstance.options.horizontalLine) {
      for (index = 0; index < chartInstance.options.horizontalLine.length; index += 1) {
        line = chartInstance.options.horizontalLine[index];

        if (!line.style) {
          style = '#ccc';
        } else {
          style = line.style;
        }

        let yValue = 0;
        if (line.y) {
          yValue = yScale.getPixelForValue(line.y);
        }

        ctx.lineWidth = 0.15;

        if (yValue) {
          ctx.beginPath();
          ctx.moveTo(29, yValue);
          ctx.lineTo(canvas.width, yValue);
          ctx.strokeStyle = style;
          ctx.stroke();
        }

        if (line.text) {
          ctx.fillStyle = style;
          ctx.fillText(line.text, 0, yValue + ctx.lineWidth);
        }
      }
    }
  },
  beforeDraw(chartInstance) {
    const ctx = chartInstance.chart.ctx;
    const chartArea = chartInstance.chartArea;

    let si = '';
    let ti = 0;
    let bgCol = [];
    let x1 = 0;
    let y1 = 0;
    let x2 = 0;
    let y2 = 0;

    if (chartInstance && chartInstance.scales) {
      // eslint-disable-next-line no-restricted-syntax
      for (si in chartInstance.scales) {
        if (si) {
          const scale = chartInstance.scales[si];
          const axisWidth = scale.options.gridLines.lineWidth;
          const isHorizontal = scale.isHorizontal();
          const gridLines = chartInstance.scales[si].options.gridLines;
          const lineWidth = gridLines.lineWidth;
          bgCol = scale.options.gridLines.backgroundColor;
          bgCol =
            bgCol === undefined
              ? chartInstance.chart.options.plugins.gridBgColor.backgroundColor
              : bgCol;
          let bgColRpt = scale.options.gridLines.backgroundColorRepeat;
          bgColRpt =
            bgColRpt === undefined
              ? chartInstance.chart.options.plugins.gridBgColor.backgroundColorRepeat
              : bgColRpt;

          if (!scale.options.display || bgCol === undefined) {
            // eslint-disable-next-line no-continue
            continue;
          }

          if (typeof bgCol === 'string') {
            ctx.fillStyle = bgCol;
            ctx.fillRect(
              chartArea.left,
              chartArea.bottom,
              chartArea.right - chartArea.left,
              chartArea.top - chartArea.bottom
            );
            // eslint-disable-next-line no-continue
            continue; // next scale
          }

          if (bgCol.constructor === Array) {
            const prevTick = {
              bgCol: '',
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 0,
            };
            let curBgCol = '';
            const numToDo = scale.ticks.length;

            for (ti = 0; ti <= numToDo; ti += 1) {
              ti = Number(ti);
              if (ti === numToDo) {
                curBgCol = 'differentFromPrevious';
              } else {
                curBgCol = bgCol[ti];
                if (bgColRpt) {
                  curBgCol = bgCol[ti % bgCol.length];
                }
              }
              if (curBgCol !== prevTick.bgCol) {
                if (isHorizontal) {
                  let xLineValue = getLineValue(
                    scale,
                    ti,
                    gridLines.offsetGridLines && scale.ticks.length > 1
                  );
                  xLineValue += lineWidth % 2 === 0 ? 0 : 0.5;
                  // eslint-disable-next-line no-multi-assign
                  x1 = x2 = xLineValue;
                  y1 = chartArea.top;
                  y2 = chartArea.bottom + axisWidth;
                } else {
                  let yLineValue = getLineValue(
                    scale,
                    ti,
                    gridLines.offsetGridLines && scale.ticks.length > 1
                  );
                  yLineValue += lineWidth % 2 === 0 ? 0 : 0.5;
                  x1 = chartArea.left;
                  x2 = chartArea.right + axisWidth;
                  // eslint-disable-next-line no-multi-assign
                  y1 = y2 = yLineValue;
                }

                if (gridLines.drawOnChartArea && prevTick.bgCol !== '') {
                  ctx.save();
                  ctx.fillStyle = prevTick.bgCol;

                  if (isHorizontal) {
                    ctx.fillRect(
                      prevTick.x1,
                      prevTick.y1,
                      Math.abs(x2 - prevTick.x1),
                      Math.abs(y2 - prevTick.y1)
                    );
                  } else {
                    ctx.fillRect(
                      prevTick.x1,
                      prevTick.y1,
                      Math.abs(x2 - prevTick.x1),
                      Math.abs(y2 - prevTick.y1)
                    );
                  }
                }
                ctx.restore();

                prevTick.bgCol = curBgCol;
                prevTick.x1 = x1;
                prevTick.y1 = y1;
                prevTick.x2 = x2;
                prevTick.y2 = y2;
              }
            }

            // eslint-disable-next-line no-continue
            continue;
          }
          console.info(
            `chartjs-plugin-gridline-background.js: Error, unknown type of backgroundColor (not String or Array): ${bgCol}`
          );
        }
      }
    }
  },
};

export const getDateFrom = (data: IGraphResponseData[]) => {
  if (data && data[0]) {
    return new Date(data[0].timestamp);
  }
  return null;
};

export const getDateTo = (data: IGraphResponseData[]) => {
  if (data && data.length > 0) {
    return new Date(data[data.length - 1].timestamp);
  }
  return null;
};

export const getClickedPointForCompressedGraph = (timestamp: string, xLabels: string[]) => {
  if (timestamp) {
    return xLabels.map((x) => {
      if (x === timestamp) {
        return 6;
      }
      return 0;
    });
  }
  return 0;
};

export const getClickedPoint = (
  datasetsData: IGraphXYData[],
  timestamp: string
): number[] | number => {
  if (timestamp) {
    return datasetsData.map((item) => {
      if (item.x === timestamp) {
        return 6;
      }
      return 0;
    });
  }
  return 0;
};

export const getDecimalCommaNumber = (value?: number | string | null): string => {
  if (value !== null && value !== undefined) {
    const newValue = Number.isInteger(Number(value)) ? value : Number(value).toFixed(1);
    return String(newValue).replace('.', ',');
  }
  return '';
};
